import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Captions, Upload, Trash2, X } from "lucide-react";

const COMMON_LANGS = [
  { code: "en", label: "English" },
  { code: "fr", label: "French" },
  { code: "sw", label: "Swahili" },
  { code: "yo", label: "Yoruba" },
  { code: "ig", label: "Igbo" },
  { code: "ha", label: "Hausa" },
  { code: "am", label: "Amharic" },
  { code: "pt", label: "Portuguese" },
  { code: "es", label: "Spanish" },
  { code: "ar", label: "Arabic" },
];

/**
 * Per-movie caption (subtitle) manager. Lets the owner upload a WebVTT file
 * per language and delete existing tracks. Cloudflare handles the player UI
 * for caption selection automatically once tracks are uploaded.
 */
export default function CaptionManager({ movie, onClose }) {
  const [captions, setCaptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("en");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/movies/${movie.id}/captions`);
      setCaptions(data.captions || []);
    } catch {
      setCaptions([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, [movie.id]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a .vtt file"); return; }
    if (!/^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$/.test(lang)) {
      toast.error("Invalid language code (e.g. en, fr, en-GB)");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("language", lang);
      form.append("file", file);
      await api.post(`/movies/${movie.id}/captions`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${lang.toUpperCase()} caption track uploaded`);
      setFile(null);
      await refresh();
    } catch (err) {
      const det = err.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (language) => {
    if (!window.confirm(`Delete ${language.toUpperCase()} caption track?`)) return;
    try {
      await api.delete(`/movies/${movie.id}/captions/${language}`);
      toast.success(`${language.toUpperCase()} track removed`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="caption-manager">
      <div className="card-surface w-full max-w-xl p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" aria-label="Close" data-testid="caption-manager-close">
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
            <Captions className="h-5 w-5 text-[#E65F2B]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-2xl font-bold leading-tight">Captions for &ldquo;{movie.title}&rdquo;</h3>
            <p className="text-sm text-white/60 mt-2">
              Upload WebVTT (.vtt) files — one per language. Captions appear automatically inside the Cloudflare player.
            </p>
          </div>
        </div>

        {/* Upload form */}
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="caption-upload-form">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3">
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="input-dark"
              data-testid="caption-language-select"
            >
              {COMMON_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label} ({l.code})</option>)}
            </select>
            <input
              type="file"
              accept=".vtt,text/vtt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="input-dark file:mr-3 file:bg-[#E65F2B] file:text-white file:border-0 file:px-3 file:py-1 file:rounded"
              data-testid="caption-file-input"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !file}
            className="ember-btn disabled:opacity-40"
            data-testid="caption-upload-button"
          >
            <Upload className="h-4 w-4" /> {busy ? "Uploading..." : "Upload caption track"}
          </button>
        </form>

        {/* Existing tracks */}
        <div className="mt-6">
          <p className="text-eyebrow mb-3">Existing tracks</p>
          {loading ? (
            <div className="h-8 w-full bg-white/5 rounded animate-pulse" />
          ) : captions.length === 0 ? (
            <p className="text-sm text-white/40" data-testid="caption-empty">No captions uploaded yet.</p>
          ) : (
            <ul className="space-y-2" data-testid="caption-list">
              {captions.map((c) => (
                <li key={c.language} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2" data-testid={`caption-row-${c.language}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm">
                      {c.label || c.language.toUpperCase()}
                      <span className="ml-2 text-white/40 text-xs">({c.language})</span>
                    </p>
                    <p className="text-[11px] text-white/40">
                      {c.status || "ready"}{c.generated ? " · AI-generated" : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => remove(c.language)}
                    className="ghost-btn !py-1 !px-3 text-xs hover:border-red-500/50 hover:text-red-400"
                    data-testid={`caption-delete-${c.language}`}
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
